// Site health monitoring and keepalive utilities
export class SiteHealthMonitor {
  private static instance: SiteHealthMonitor;
  private healthCheckInterval: NodeJS.Timeout | null = null;
  private lastHealthCheck: Date | null = null;
  private isOnline = navigator.onLine;
  private retryAttempts = 0;
  private maxRetries = 2; // Reduced from 3

  private constructor() {
    this.setupOnlineDetection();
    this.startHealthChecks();
  }

  static getInstance(): SiteHealthMonitor {
    if (!SiteHealthMonitor.instance) {
      SiteHealthMonitor.instance = new SiteHealthMonitor();
    }
    return SiteHealthMonitor.instance;
  }

  private setupOnlineDetection() {
    window.addEventListener('online', () => {
      console.log('🌐 Network connection restored');
      this.isOnline = true;
      this.retryAttempts = 0;
      this.performHealthCheck();
    });

    window.addEventListener('offline', () => {
      console.log('📡 Network connection lost');
      this.isOnline = false;
    });
  }

  private startHealthChecks() {
    // Perform initial health check
    this.performHealthCheck();
    
    // Set up periodic health checks (every 10 minutes - less aggressive)
    this.healthCheckInterval = setInterval(() => {
      this.performHealthCheck();
    }, 10 * 60 * 1000);
  }

  private async performHealthCheck() {
    if (!this.isOnline) return;

    try {
      console.log('🔍 Performing site health check...');
      
      // Use a more reliable endpoint check with proper caching
      const response = await fetch(window.location.origin + '/favicon.ico', {
        method: 'HEAD',
        cache: 'default', // Allow browser caching
        signal: AbortSignal.timeout(5000), // Reduced timeout
      });

      if (response.ok) {
        console.log('✅ Site health check passed');
        this.lastHealthCheck = new Date();
        this.retryAttempts = 0;
      } else {
        throw new Error(`Health check failed: ${response.status}`);
      }
    } catch (error) {
      console.warn('⚠️ Site health check failed:', error);
      this.retryAttempts++;
      
      if (this.retryAttempts <= this.maxRetries) {
        console.log(`🔄 Retrying health check (${this.retryAttempts}/${this.maxRetries})...`);
        setTimeout(() => this.performHealthCheck(), 60000); // Longer retry delay
      }
    }
  }

  public getHealthStatus() {
    return {
      isOnline: this.isOnline,
      lastHealthCheck: this.lastHealthCheck,
      retryAttempts: this.retryAttempts,
    };
  }

  public forceHealthCheck() {
    this.performHealthCheck();
  }

  public destroy() {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
  }
}

// SSL Error Recovery Helper - Simplified
export class SSLErrorRecovery {
  private static instance: SSLErrorRecovery;

  static getInstance(): SSLErrorRecovery {
    if (!SSLErrorRecovery.instance) {
      SSLErrorRecovery.instance = new SSLErrorRecovery();
    }
    return SSLErrorRecovery.instance;
  }

  // Simple fetch wrapper - minimal interference with browser SSL handling
  public async resilientFetch(
    url: string | URL | Request, 
    options: RequestInit = {}
  ): Promise<Response> {
    // Single attempt with reasonable timeout
    return fetch(url, {
      ...options,
      signal: AbortSignal.timeout(30000), // Reduced from 60s
    });
  }
}

// Keepalive service to prevent cold starts
export class KeepaliveService {
  private static instance: KeepaliveService;
  private pingInterval: NodeJS.Timeout | null = null;

  static getInstance(): KeepaliveService {
    if (!KeepaliveService.instance) {
      KeepaliveService.instance = new KeepaliveService();
    }
    return KeepaliveService.instance;
  }

  public start() {
    if (this.pingInterval) return;

    console.log('🔄 Starting keepalive service...');
    
    // Send keepalive ping every 8 minutes (less aggressive)
    this.pingInterval = setInterval(async () => {
      try {
        const response = await fetch(window.location.origin + '/favicon.ico', {
          method: 'HEAD',
          cache: 'default', // Allow caching
          signal: AbortSignal.timeout(3000), // Shorter timeout
        });
        
        if (response.ok) {
          console.log('💓 Keepalive ping successful');
        }
      } catch (error) {
        // Silently fail - don't spam console
        console.debug('⚠️ Keepalive ping failed:', error);
      }
    }, 8 * 60 * 1000);
  }

  public stop() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
      console.log('⏹️ Keepalive service stopped');
    }
  }

  public destroy() {
    this.stop();
  }
}

// Simplified SSL error detection
export function isSSLRelatedError(error: any): boolean {
  if (!error) return false;
  
  const errorMessage = error.message || error.toString() || '';
  
  // Only catch the most critical SSL errors
  return errorMessage.includes('net::ERR_SSL_PROTOCOL_ERROR') ||
         errorMessage.includes('ERR_SSL_VERSION_OR_CIPHER_MISMATCH') ||
         errorMessage.includes('SSL_ERROR_SSL');
}

// Minimal global error handler - no interference with browser SSL
export function setupGlobalSSLErrorHandling() {
  // Only log critical SSL errors, don't interfere
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    if (isSSLRelatedError(error)) {
      console.debug('🔒 SSL-related error logged:', error);
      // Never prevent default - let browser handle SSL naturally
    }
  });
}